import { AESCipherSIVDeterministic } from '../../base/cipher/aes';
import { AbstractSelfAccountKeyring } from '../../keyring/account_base';
import { PeerAccountKeyring } from '../../keyring/account_peer';
import { AbstractRoomKeyring } from '../../keyring/room_base';
import { AbstractCryptoConfig, CryptoConfigVersioning } from '../base';
import { AccountPairDeriveKeyV2, KemSharedSecretSelectorV1 } from '../common';
import { RoomKeyConfigDumpArgs, RoomKeyConfigLoadArgs } from './args';
import { makeInfoByteArray } from '../../utility';
import { CryptoUpgrade } from '../feature';

/**
 * Room key encryption: AES-SIV
 *         KEY: 32B from curve25519 scalar_mult + 32B from MLKEM1024 (ss-id encoded in version by acc-id)
 *             -> 64B HKDF by context
 *         NONCE: none
 *
 *     Encryption of the keys required for room access. These are always distributed by the owner with P2P encryption
 *     for other peer accounts.
 *     NOTE: No LRU cache on derive_key(), room keys are only loaded once and they are unique so there's no benefit.
 */
class _RoomKeyConfigV2V3 extends AbstractCryptoConfig<
  RoomKeyConfigLoadArgs,
  RoomKeyConfigDumpArgs
> {
  public other_version: Uint8Array;

  constructor(version: number, other_version: number, cu: CryptoUpgrade) {
    const other_config_version = CryptoConfigVersioning.config_version_from_int(other_version);
    const config_version = CryptoConfigVersioning.config_version_from_int(version);
    if (other_config_version === null || config_version === null) {
      throw new Error('Invalid version number for crypto config');
    }
    super(version, cu, [config_version, other_config_version]);
    this.other_version = other_config_version;
  }

  public load(args: RoomKeyConfigLoadArgs): Promise<Uint8Array> {
    let cipher = args.cipher.slice(this.version.byteLength);

    return this.derive_key(
      KemSharedSecretSelectorV1.select_ss_for_load(
        args.self_kr,
        args.peer_kr,
        this.version,
        this.other_version
      ),
      this.version,
      args.self_kr,
      args.peer_kr,
      args.room_kr,
      args.key_version
    ).then((derive_key) => {
      return AESCipherSIVDeterministic.decrypt(cipher, derive_key);
    });
  }

  public dump(args: RoomKeyConfigDumpArgs): Promise<Uint8Array> {
    const ssv = KemSharedSecretSelectorV1.select_ss_version_for_dump(
      args.self_kr,
      args.peer_kr,
      this.version,
      this.other_version
    );
    return this.derive_key(
      ssv.shared_secret,
      ssv.selected_version,
      args.self_kr,
      args.peer_kr,
      args.room_kr,
      args.key_version
    ).then((derive_key) => {
      return AESCipherSIVDeterministic.encrypt(
        args.room_kr.get_key(args.key_version),
        derive_key,
        undefined,
        ssv.selected_version
      );
    });
  }

  public derive_key(
    shared_secret: Uint8Array,
    version: Uint8Array,
    self_kr: AbstractSelfAccountKeyring,
    peer_kr: PeerAccountKeyring,
    room_kr: AbstractRoomKeyring,
    key_version: string
  ): Promise<Uint8Array> {
    return AccountPairDeriveKeyV2.derive_key(
      shared_secret,
      self_kr,
      peer_kr,
      makeInfoByteArray(['room-key-', version, '-', room_kr.id, '-', key_version]),
      false
    );
  }
}

export class RoomKeyConfigV2 extends _RoomKeyConfigV2V3 {
  constructor(cu: CryptoUpgrade) {
    super(2, 3, cu);
  }
}

export class RoomKeyConfigV3 extends _RoomKeyConfigV2V3 {
  constructor(cu: CryptoUpgrade) {
    super(3, 2, cu);
  }
}
